/**
 * Plateforme multi événement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/i18n/fr.demandezAuSalon.ts
 *
 * Messages de « Demandez à l’événement » (/admin/demandez) : une question
 * en français, une réponse chiffrée tirée d’une liste fermée d’analyses
 * (api/admin/demandez-au-salon.php). Les phrases « calculées sans IA » sont
 * les gabarits ci-dessous, remplis avec les chiffres du serveur.
 *
 * Vocabulaire de l’événement par jetons, jamais en dur : {eventTypeAt}
 * donne « au salon » pour un salon, {lexWorkshop…} et {lexExhibitor…} le
 * mot choisi par l’organisation. Aucune apostrophe droite devant un jeton.
 * Fusionné dans fr.ts.
 *
 * Mikael - chef de projet - 2026-10-04
 */
export const messagesDemandezAuSalonFr: Record<string, string> = {
  // ── Menu et titre de document ────────────────────────────────────────
  'adminNav.demandez': 'Demandez {eventTypeAt}',
  'acces.titre.adminDemandez': 'Demandez {eventTypeAt}',

  // ── En-tête ──────────────────────────────────────────────────────────
  'demandezAuSalon.titre': 'Demandez {eventTypeAt}',
  'demandezAuSalon.sousTitre':
    'Posez une question en français : la réponse est calculée sur les chiffres réels, sans aucune donnée personnelle.',
  'demandezAuSalon.verification': 'Vérification de vos droits…',
  'demandezAuSalon.sansDroit':
    'Cette page n’est pas ouverte à votre compte. Elle s’accorde dans Contrôle d’accès, par rôle ou par compte.',

  // ── Saisie ───────────────────────────────────────────────────────────
  'demandezAuSalon.champ.libelle': 'Votre question',
  'demandezAuSalon.champ.exemple': 'Par exemple : combien de passages depuis 10 h ?',
  'demandezAuSalon.champ.aide': 'Entrée pour envoyer, Maj + Entrée pour aller à la ligne.',
  'demandezAuSalon.bouton': 'Demander',
  'demandezAuSalon.bouton.enCours': 'Calcul en cours…',
  'demandezAuSalon.suggestions.titre': 'Idées de questions',
  'demandezAuSalon.suggestion.1': 'Quels stands ont le plus de passages depuis 10 h ?',
  'demandezAuSalon.suggestion.2': 'Combien de CV déposés ce matin ?',
  'demandezAuSalon.suggestion.3': 'Où reste-t-il peu de places dans {lexWorkshopDefPlural} ?',
  'demandezAuSalon.suggestion.4': 'Combien de personnes sont présentes en ce moment ?',
  'demandezAuSalon.suggestion.5': 'Quels stands n’ont encore aucun passage ?',
  'demandezAuSalon.suggestion.6': 'Combien d’offres d’emploi par secteur ?',

  // ── Réponse ──────────────────────────────────────────────────────────
  'demandezAuSalon.reponse.titre': 'Réponse',
  'demandezAuSalon.reponse.question': 'Question : {question}',
  'demandezAuSalon.badge.ia': 'Réponse rédigée par l’IA',
  'demandezAuSalon.badge.regles': 'Réponse calculée sans IA',
  'demandezAuSalon.badge.aide':
    'Les chiffres sont toujours calculés par le serveur. L’IA ne fait que choisir l’analyse et rédiger la phrase.',
  'demandezAuSalon.chiffresAu': 'Chiffres au {date} à {heure}',
  'demandezAuSalon.masque': 'moins de 3',
  'demandezAuSalon.nonCalculable': 'non calculable',
  'demandezAuSalon.aucun': 'aucun',
  'demandezAuSalon.nonMesure': 'Ce chiffre n’est pas mesuré sur cette instance.',
  'demandezAuSalon.aucuneAnalyse':
    'Je n’ai pas trouvé d’analyse qui réponde à cette question. Reformulez-la, ou choisissez une des analyses ci-dessous.',
  'demandezAuSalon.tableau.titre': 'Les chiffres',
  'demandezAuSalon.tableau.vide': 'Aucune ligne à afficher.',
  'demandezAuSalon.analyseRetenue': 'Analyse retenue : {analyse}',
  'demandezAuSalon.erreur.generique':
    'Les chiffres sont momentanément indisponibles. Réessayez dans un instant.',
  'demandezAuSalon.erreur.catalogue':
    'La liste des analyses est momentanément indisponible. Vous pouvez tout de même poser votre question.',
  'demandezAuSalon.ia.indisponible':
    'L’IA n’est pas disponible pour le moment : les réponses sont calculées sans IA, avec les mêmes chiffres.',
  'demandezAuSalon.seuil':
    'Par confidentialité, un groupe de moins de 3 personnes s’affiche « moins de 3 », et les mineurs ne sont jamais comptés dans les chiffres de CV. Chaque question est inscrite au journal, sans la réponse.',

  // ── Périodes ─────────────────────────────────────────────────────────
  'demandezAuSalon.periode.aujourdhui': 'aujourd’hui',
  'demandezAuSalon.periode.debut': 'depuis le début',
  'demandezAuSalon.periode.plage': 'aujourd’hui de {de} à {a}',
  'demandezAuSalon.periode.depuis': 'aujourd’hui depuis {de}',

  // ── Catalogue ────────────────────────────────────────────────────────
  'demandezAuSalon.catalogue.titre': 'Ou choisissez une analyse',
  'demandezAuSalon.categorie.affluence': 'Affluence',
  'demandezAuSalon.categorie.stands': 'Stands',
  'demandezAuSalon.categorie.ateliers': '{lexWorkshopPluralCap}',
  'demandezAuSalon.categorie.emploi': 'Emploi et CV',
  'demandezAuSalon.categorie.inscrits': 'Inscriptions',

  'demandezAuSalon.analyse.presents_maintenant': 'Présents en ce moment',
  'demandezAuSalon.analyse.passages_total': 'Passages du jour',
  'demandezAuSalon.analyse.affluence_quart_heure': 'Affluence par quart d’heure',
  'demandezAuSalon.analyse.comparaison_heure': 'Dernière heure contre la précédente',
  'demandezAuSalon.analyse.entrees_controles': 'Points de contrôle',
  'demandezAuSalon.analyse.top_stands': 'Stands les plus visités',
  'demandezAuSalon.analyse.stands_sans_passage': 'Stands sans passage',
  'demandezAuSalon.analyse.scans_exposants': 'Scans faits par {lexExhibitorDefPlural}',
  'demandezAuSalon.analyse.fiches_entretien': 'Fiches d’entretien',
  'demandezAuSalon.analyse.ateliers_remplissage': 'Remplissage',
  'demandezAuSalon.analyse.ateliers_presque_complets': 'Presque au complet',
  'demandezAuSalon.analyse.ateliers_complets': 'Au complet',
  'demandezAuSalon.analyse.offres_total': 'Offres d’emploi',
  'demandezAuSalon.analyse.offres_par_secteur': 'Offres par secteur',
  'demandezAuSalon.analyse.cv_deposes': 'CV déposés',
  'demandezAuSalon.analyse.cv_ouverts': 'CV consultés',
  'demandezAuSalon.analyse.candidatures': 'Candidatures',
  'demandezAuSalon.analyse.inscrits_total': 'Inscrits',
  'demandezAuSalon.analyse.inscrits_par_profil': 'Inscrits par type de public',
  'demandezAuSalon.analyse.inscrits_par_jour': 'Inscriptions par jour',

  // ── Colonnes ─────────────────────────────────────────────────────────
  'demandezAuSalon.colonne.libelle': 'Libellé',
  'demandezAuSalon.colonne.passages': 'Passages',
  'demandezAuSalon.colonne.personnes': 'Personnes distinctes',
  'demandezAuSalon.colonne.scans': 'Scans',
  'demandezAuSalon.colonne.fiches': 'Fiches',
  'demandezAuSalon.colonne.inscrits': 'Inscrits',
  'demandezAuSalon.colonne.capacite': 'Capacité',
  'demandezAuSalon.colonne.taux': 'Remplissage',
  'demandezAuSalon.colonne.attente': 'Liste d’attente',
  'demandezAuSalon.colonne.offres': 'Offres',
  'demandezAuSalon.colonne.postes': 'Postes',
  'demandezAuSalon.colonne.mineurs': 'Dont mineurs',

  'demandezAuSalon.libelle.affluence_quart_heure': 'Quart d’heure',
  'demandezAuSalon.libelle.entrees_controles': 'Point de contrôle',
  'demandezAuSalon.libelle.top_stands': 'Stand',
  'demandezAuSalon.libelle.stands_sans_passage': '{lexExhibitorCap}',
  'demandezAuSalon.libelle.scans_exposants': '{lexExhibitorCap}',
  'demandezAuSalon.libelle.fiches_entretien': 'Décision',
  'demandezAuSalon.libelle.ateliers_remplissage': '{lexWorkshopCap}',
  'demandezAuSalon.libelle.ateliers_presque_complets': '{lexWorkshopCap}',
  'demandezAuSalon.libelle.ateliers_complets': '{lexWorkshopCap}',
  'demandezAuSalon.libelle.offres_par_secteur': 'Secteur',
  'demandezAuSalon.libelle.inscrits_par_profil': 'Type de public',
  'demandezAuSalon.libelle.inscrits_par_jour': 'Jour',

  // ── Chiffres principaux (tuiles) ─────────────────────────────────────
  'demandezAuSalon.chiffre.presents': 'Présents',
  'demandezAuSalon.chiffre.fenetre': 'Minutes de la fenêtre',
  'demandezAuSalon.chiffre.passages': 'Passages',
  'demandezAuSalon.chiffre.personnes': 'Personnes distinctes',
  'demandezAuSalon.chiffre.pic_tranche': 'Quart d’heure le plus chargé',
  'demandezAuSalon.chiffre.pic_passages': 'Passages au pic',
  'demandezAuSalon.chiffre.derniere_heure': 'Dernière heure',
  'demandezAuSalon.chiffre.heure_precedente': 'Heure précédente',
  'demandezAuSalon.chiffre.evolution': 'Évolution (%)',
  'demandezAuSalon.chiffre.scans': 'Scans',
  'demandezAuSalon.chiffre.stands': 'Stands avec passage',
  'demandezAuSalon.chiffre.sans_passage': 'Sans passage',
  'demandezAuSalon.chiffre.publies': 'Fiches publiées',
  'demandezAuSalon.chiffre.structures': 'Structures',
  'demandezAuSalon.chiffre.fiches': 'Fiches créées',
  'demandezAuSalon.chiffre.renseignees': 'Fiches renseignées',
  'demandezAuSalon.chiffre.liees_entretien': 'Liées à un entretien planifié',
  'demandezAuSalon.chiffre.confirmees': 'Inscriptions confirmées',
  'demandezAuSalon.chiffre.attente': 'Liste d’attente',
  'demandezAuSalon.chiffre.nombre': '{lexWorkshopPluralCap}',
  'demandezAuSalon.chiffre.offres': 'Offres',
  'demandezAuSalon.chiffre.postes': 'Postes',
  'demandezAuSalon.chiffre.secteurs': 'Secteurs',
  'demandezAuSalon.chiffre.cv': 'CV',
  'demandezAuSalon.chiffre.consultations': 'Consultations',
  'demandezAuSalon.chiffre.cv_distincts': 'CV distincts',
  'demandezAuSalon.chiffre.candidatures': 'Candidatures',
  'demandezAuSalon.chiffre.inscrits': 'Inscrits',
  'demandezAuSalon.chiffre.mineurs': 'Dont mineurs',

  // ── Gabarits des phrases calculées sans IA ───────────────────────────
  'demandezAuSalon.gabarit.presents_maintenant':
    'Personnes présentes en ce moment (badge scanné dans les {fenetre} dernières minutes) : {presents}.',
  'demandezAuSalon.gabarit.passages_total':
    'Passages enregistrés {periode} : {passages}. Personnes distinctes : {personnes}.',
  'demandezAuSalon.gabarit.affluence_quart_heure':
    'Passages enregistrés {periode} : {passages}. Quart d’heure le plus chargé : {pic_tranche}, avec {pic_passages} passages.',
  'demandezAuSalon.gabarit.affluence_quart_heure.vide': 'Aucun passage enregistré {periode}.',
  'demandezAuSalon.gabarit.comparaison_heure':
    'Dernière heure : {derniere_heure} passages, contre {heure_precedente} l’heure précédente, soit une évolution de {evolution} %.',
  'demandezAuSalon.gabarit.comparaison_heure.sansBase':
    'Dernière heure : {derniere_heure} passages, contre {heure_precedente} l’heure précédente.',
  'demandezAuSalon.gabarit.entrees_controles':
    'Points de contrôle, {periode} : {scans} scans, pour {personnes} personnes distinctes.',
  'demandezAuSalon.gabarit.top_stands':
    'Stands avec au moins un passage {periode} : {stands}, pour {passages} passages. Le classement est ci-dessous.',
  'demandezAuSalon.gabarit.top_stands.vide': 'Aucun passage enregistré sur les stands {periode}.',
  'demandezAuSalon.gabarit.stands_sans_passage':
    'Sans aucun passage {periode} : {sans_passage} sur {publies} {lexExhibitorPlural}.',
  'demandezAuSalon.gabarit.scans_exposants':
    'Scans faits par {lexExhibitorDefPlural} {periode} : {scans}, par {structures} structures, pour {personnes} personnes distinctes.',
  'demandezAuSalon.gabarit.fiches_entretien':
    'Fiches d’entretien créées {periode} : {fiches}, dont {renseignees} renseignées et {liees_entretien} liées à un entretien planifié.',
  'demandezAuSalon.gabarit.ateliers_remplissage':
    'Inscriptions confirmées {lexWorkshopAtPlural} : {confirmees}, et {attente} en liste d’attente. Le remplissage détaillé est ci-dessous.',
  'demandezAuSalon.gabarit.ateliers_presque_complets':
    '{lexWorkshopPluralCap} presque au complet (85 % de la capacité ou plus) : {nombre}.',
  'demandezAuSalon.gabarit.ateliers_presque_complets.vide':
    'Pour l’instant, {lexWorkshopNone} n’est presque au complet.',
  'demandezAuSalon.gabarit.ateliers_complets': '{lexWorkshopPluralCap} au complet : {nombre}.',
  'demandezAuSalon.gabarit.ateliers_complets.vide': 'Pour l’instant, {lexWorkshopNone} n’est au complet.',
  'demandezAuSalon.gabarit.offres_total':
    'Offres d’emploi publiées : {offres}, pour {postes} postes proposés par {structures} structures.',
  'demandezAuSalon.gabarit.offres_par_secteur':
    'Offres d’emploi publiées : {offres}, réparties sur {secteurs} secteurs.',
  'demandezAuSalon.gabarit.cv_deposes':
    'CV déposés {periode} : {cv}, par {personnes} personnes (personnes majeures uniquement).',
  'demandezAuSalon.gabarit.cv_ouverts':
    'Consultations de CV par les recruteurs {periode} : {consultations}, sur {cv_distincts} CV distincts, par {structures} structures.',
  'demandezAuSalon.gabarit.candidatures':
    'Candidatures envoyées {periode} : {candidatures}, par {personnes} personnes, à {structures} structures.',
  'demandezAuSalon.gabarit.inscrits_total': 'Personnes inscrites : {inscrits}, dont {mineurs} mineurs.',
  'demandezAuSalon.gabarit.inscrits_par_profil':
    'Personnes inscrites : {inscrits}. La répartition par type de public est ci-dessous.',
  'demandezAuSalon.gabarit.inscrits_par_jour': 'Nouvelles inscriptions sur les jours affichés : {inscrits}.',
};
